import { memo, useMemo, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    CircularProgress,
    Tooltip,
    Alert
} from '@mui/material';
import { useVehicleConflictStore } from '../stores/useVehicleConflictStore';
import { useVehiclesStore } from '../stores/useVehiclesStore';
import { useLinesStore } from '../stores/useLinesStore';
import useEnsureVehicleValidation from '../hooks/vehicles/useEnsureVehicleValidation';
import CustomSelect from './CustomSelect';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import VpnKey from '@mui/icons-material/VpnKey';

function VehicleConflictItem({
    tripId,
    depth = 0
}) {

    const node = useVehicleConflictStore(state =>
        state.getNode(tripId)
    );

    const vehicles = useVehiclesStore(
        state => state.veiculosDisponiveis
    );

    const isVehicleUsed = useVehicleConflictStore(
        state => state.isVehicleUsed
    );

    const veiculosDisponiveis = useMemo(() => [

        {
            id: 'NEWSEL',
            label: 'Selecione um novo veículo'
        },

        ...vehicles.filter(vehicle =>

            vehicle.id === node?.newVehicle ||

            !isVehicleUsed(vehicle.id)

        )

    ], [
        vehicles,
        node?.newVehicle,
        isVehicleUsed
    ]);

    const updateVehicle = useVehicleConflictStore(
        state => state.updateVehicle
    );

    const {
        loadingVehicle,
        vehicleConflicts,
        hasConflictVehicle,
        validated
    } = useEnsureVehicleValidation({
        tripId: node?.tripId,
        codigoIntegracao: node?.codigoIntegracao,
        timeCaught: node?.timeCaught,
        vehicle: node?.newVehicle,
        startAt: node?.startAt,
        endAt: node?.endAt
    });

    useEffect(() => {

        if (!validated) return;

        if (loadingVehicle) return;

        if (!node.newVehicle) {
            return;
        }

        const store = useVehicleConflictStore.getState();

        store.setVehicleChange(
            node.tripId,
            node.originalVehicle,
            node.newVehicle,
            node.startAt,
            node.codigoIntegracao
        );

        if (!hasConflictVehicle) {

            store.setResolved(
                node.tripId,
                true
            );
            
        } else {

            store.setResolved(
                node.tripId,
                false
            );

            store.addChildren(
                node.tripId,
                vehicleConflicts
            );

        }

        store.updateResolvedUpwards(node.tripId);

    }, [
        validated,
        loadingVehicle,
        hasConflictVehicle,
        vehicleConflicts,
        node?.tripId,
        node?.originalVehicle,
        node?.newVehicle,
        node?.startAt,
        node?.codigoIntegracao
    ]);

    const linha = useLinesStore(state =>
        state.linhasDisponiveisMap.get(String(node?.codigoIntegracao))
    );

    const linhaLabel = linha
        ? `${linha.PREFIXO} | ${linha.NOMELINHA}`
        : node?.codigoIntegracao || 'Sem linha';

    const isResolved = useVehicleConflictStore(state =>
        state.isResolved(tripId)
    );

    if (!node) {
        return null;
    }

    const children = node.children ?? [];

    const iniPrev = node?.startAt?.slice(11, 16) || '--:--';
    const fimPrev = node?.endAt?.slice(11, 16) || '--:--';
    const timeCaught = node?.timeCaught?.slice(11, 16) || '--:--';
    

    return (
        <Box
            sx={(theme) => ({
                mb: 1,
                border: depth === 0 ? '2px solid' : '2px dashed',
                borderColor: isResolved
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                borderRadius: 1,
                p: 1.5,
                ml: depth * 2,
                backgroundColor: theme.palette.background.default
            })}
        >
            <Typography
                fontWeight="bold"
                color={isResolved ? 'success.main': 'error.main'}
            >
                {linhaLabel} - {node.codigoIntegracao}
            </Typography>

            {!node.canAdjustNow && node.editingLock && (

                <Alert severity="warning">

                    A viagem está sendo editada por {" "}
                    <strong>{node.editingLock.userName}</strong>.

                </Alert>

            )}
            <Box mt={1}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                        alignItems: 'center',
                        flexWrap: 'wrap'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Início Previsto">
                            <AlarmOnIcon sx={{ fontSize: 20, color: 'success.main' }} />
                        </Tooltip>

                        <Typography sx={{ ml: -1.5, fontWeight: 'bold', color: 'success.main' }}>
                            P
                        </Typography>

                        <Typography>{iniPrev}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Final Previsto">
                            <AlarmOffIcon sx={{ fontSize: 20, color: 'error.main' }} />
                        </Tooltip>

                        <Typography sx={{ ml: -1.5, fontWeight: 'bold', color: 'error.main' }}>
                            P
                        </Typography>

                        <Typography>{fimPrev}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Hora Pegada">
                            <VpnKey sx={{ fontSize: 26, color: 'warning.main' }} />
                        </Tooltip>

                        <Typography>{timeCaught}</Typography>
                    </Box>                  
                </Box>
            </Box>

            <CustomSelect
                items={veiculosDisponiveis}
                selected={node.newVehicle ?? 'NEWSEL'}
                onChange={vehicle =>
                    updateVehicle(
                        tripId,
                        vehicle
                    )
                }
                label="Novo veículo"
                allowNone={false}
                enableSearch
                layout="minor"
                scrollToSelected
                disabled={!node.canAdjustNow}
            />
            {loadingVehicle && (
                <Box
                    sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <CircularProgress size={20} />

                    <Typography>
                        Verificando conflitos...
                    </Typography>
                </Box>
            )}

            {children.map(childId => (
                <VehicleConflictItem
                    key={childId}
                    tripId={childId}
                    depth={depth + 1}
                />
            ))}
        </Box>
    );
}

export default memo(VehicleConflictItem);